import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface SearchResult {
  [key: string]: any;
}

interface SearchResultsProps {
  results: Record<string, SearchResult[]>;
  operatorInfo?: {
    phone: string;
    operator: string;
    region: string;
    country: string;
  };
  queryTime?: number;
  totalResults?: number;
}

export function SearchResults({ results, operatorInfo, queryTime, totalResults }: SearchResultsProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Словарь приоритетов для сортировки полей
  const fieldPriorities: Record<string, number> = {
    // ФИО
    'Имя': 1,
    'Фамилия': 2, 
    'Отчество': 3,
    'Полное имя': 4,
    
    // Дата рождения
    'Дата рождения': 5,
    'Дата рождения (возможная опечатка)': 6,
    
    // Контактная информация
    'Телефон': 7,
    'номер (ед. число)': 8,
    'Рабочий номер': 9,
    'Номер ближайшего человека': 11,
    
    // Email
    'Email': 18,
    'Контактный E-mail': 19,
    'E-mail для входа': 20,
    
    // Документы
    'Паспорт': 21,
    'Данные паспорта': 22,
    'Дата паспорта': 23,
    'Серия и номер паспорта': 24,
    'Дата выдачи паспорта': 25,
    'Кем выдан паспорт': 26,
    'Серия документа': 27,
    'Номер документа': 28,
    'Тип документа': 29,
    'Дата выдачи документа': 30,
    'Кем выдан документ': 31,
    
    // Другие важные документы
    'ИНН': 33,
    'СНИЛС': 34,
    
    // Адреса
    'Адрес': 36,
    'Полный адрес': 37,
    'Адрес регистрации': 38,
    'Фактический адрес проживания': 39,
    'Адрес постоянной регистрации': 40,
    'Временный адрес регистрации': 41,
    'Адрес работы': 42,
  };

  // Автоматический скролл к записи при загрузке, если есть хеш в URL
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith('#record-')) {
      const element = document.getElementById(hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Добавляем подсветку для найденной записи
          element.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
          }, 3000);
        }, 100);
      }
    }
  }, [results]);

  // Отслеживание скролла для показа кнопки "Вверх"
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Функция для прокрутки наверх
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const sourceNames: Record<string, string> = {
    'ruton': 'ВЗЛОМ RUTON',
    'sportmaster': 'SPORTMASTER.RU КЛИЕНТЫ',
    'tehnodepart2': 'ДЕПАРТАМЕНТ ИНФОРМАЦИОННЫХ ТЕХНОЛОГИЙ',
    'fssp': 'ФССП',
    'russian': 'СБОРКА ЛИМИТА НА МИКРОЗАЙМЫ',
    'rosreestr2025': 'РОСРЕЕСТР 2025',
    'mfo': 'КЛЕНТЫ МФО 2013 - 2020',
    'ipchink': 'ИНДИВИДУАЛЬНЫЕ ПРЕДПРИНИМАТЕЛИ 2022',
    'ispolnitelnaja': 'ЗАЯВКИ НА МИКРОЗАЙМЫ OCM&DS&CTY',
    'krimianl': 'РЕЕСТР КРИМИНАЛЬНЫХ ДЕЛ',
    'skoringfull': 'КРЕДИТНЫЙ СКОРИНГ',
    'rusprofile': 'БАЗА ИНН RUSPROFILE.RU',
    'fssp2019': 'ДОЛЖНИКИ ФЛ ФССП 2019',
    'income2018': 'ПОЛЬЗОВАТЕЛИ HH.RU 2018',
    'gemotest': 'РФ ТЕЛЕФОНЫ КЛИЕНТЫ ГЕМОТЕСТ 2022',
    'sauron': 'БАЗА САУРОНА',
    'emias': 'МЕДИЦИНСКОЕ СТРАХОВАНИЕ ФЛ 2024',
    'tg_2022': 'ПОЛЬЗОВАТЕЛИ ТЕЛЕГРАМ 2022',
    'alpha-insurance': 'КЛИЕНТЫ ALFASTRAH.RU 2023',
    'basebyruton': 'СБОРКА RUTON',
    'bigbase': 'СБОРКА RUTON',
    'carbinatelecom': 'ТЕЛЕФОНЫ АБОНЕНТЫ КОРБИНА ТЕЛЕКОМ 2004-2021',
    'criminal': 'КРИМИНАЛ РОССИЯ',
    'finfive': 'КЛИЕНТЫ FINFIVE.RU 2022',
    'helix': 'КЛИЕНТЫ HELIX.RU 2023',
    'income2011': 'ДОХОДЫ 2005 - 2011',
    'ipotechniki': 'РЕЕСТР ИПОТЕЧНИКОВ',
    'kontagents': 'КОНТРААГЕНТЫ XFIRM.RU 2024',
    'mfo2': 'КЛИЕНТЫ МФО 2013 - 2020',
    'mfo3': 'КЛИЕНТЫ МФО 2013 - 2020',
    'mfovivadengi': 'ПОЛЬЗОВАТЕЛИ VIVADENGI.RU',
    'mts': 'КЛИЕНТЫ МТС БАНКА',
    'nbki': 'ПОЛЬЗОВАТЕЛИ NBKI.RU 2024',
    'optekaru': 'ORTEKA.RU ПОЛЬЗОВАТЕЛИ',
    'perm': 'ЖИТЕЛИ ПЕРМИ',
    'rechflot': 'СФР ДОКУМЕНТЫ 2000 - 12.2023',
    'sberprime': 'КРЕДИТ СБЕРБАНК КЛИЕНТЫ СТАНДАРТ 2021',
    'techodepartament2': 'ДЕПАРТАМЕНТ ИНФОРМАЦИОННЫХ ТЕХНОЛОГИЙ',
    'trips': 'ПОЛЬЗОВАТЕЛИ DOBRO.RU 2022',
    'uteir': 'КЛИЕНТЫ UTEIR.RU 2021',
    'vtb-privilege': 'КЛИЕНТЫ ВТБ ПРИВИЛЕГИЯ',
    'leader-id-ru': 'LEADER-ID.RU ПОЛЬЗОВАТЕЛИ 2023',
    'workplace2': 'ПОЛЬЗОВАТЕЛИ DOBRO.RU 2022',
    'yandexdelivery': 'ПОЛЬЗОВАТЕЛИ YANDEX.DELIVERY',
    'zoloto': 'ПОЛЬЗОВАТЕЛИ ZOLOTO585.RU',
    'apartments': 'КОНТИНГЕНТ РФ 2023',
    'aviafly': 'NLSTAR.COM КЛИЕНТЫ 2022',
    'farma': 'КЛИЕНТЫ PHARMACIA 2023',
    'gosuslugi': 'ФЛ ЕСИА ГОСУСЛУГИ 2022',
    'ipoteka': 'ЗАЯВКИ НА МИКРОЗАЙМЫ OCM&DS&CTY',
    'lukoil2022': 'ПОЛЬЗОВАТЕЛИ LUKOIL.RU 2022',
    'mskpeople2': 'ЖИТЕЛИ МОСКВЫ 2023',
    'mskschool': 'ШКОЛЫ МОСКВЫ 2023',
    'reestr2023': 'РОСРЕЕСТР 2023',
    'sbornik': 'СБОРКА RUTON',
    'sbornik2': 'СБОРКА RUTON',
    'schoolbarsgroup': 'SCHOOL.BARS.GROUP ПОЛЬЗОВАТЕЛИ 2023',
    'shopdep': 'ВЗЛОМ RUTON',
    'strahovka': 'СТРАХОВАЯ КОМПАНИЯ 2022',
    'usersbox': 'ВЗЛОМ RUTON',
    'voronez': 'ЖИТЕЛИ ВОРОНЕЖА 2022',
    'vtb': 'КЛИЕНТЫ ВТБ 2021',
    'zoloto585': 'ПОЛЬЗОВАТЕЛИ ZOLOTO585.RU',
    'lukoil': 'ПОЛЬЗОВАТЕЛИ LUKOIL.RU 2022',
    'abonents': 'РФ ТЕЛЕФОНЫ МОБИЛЬНЫЕ БОЛЬШАЯ ЧЕТВЕРКА 2019',
    'rutono2': 'СБОРКА RUTON',
    'russianpeople-combined': 'СБОРКА RUTON',
    'sberbank': 'КЛИЕНТЫ СБЕРБАНКА 2023',
    'adengi': 'ADENGI.RU ПОЛЬЗОВАТЕЛИ 02.2024',
    'residents-mos-2011': 'ЖИТЕЛИ МОСКВЫ 2011',
    'booklovers': 'ПОЛЬЗОВАТЕЛИ CHITAI-GOROD.RU 2023',
    'fullscoring': 'ПОЛНЫЙ СКОРИНГ',
    'foms_kemerovo': 'ФОМС КЕМЕРОВО',
    'foms-rostov-2008': 'ФОМС РОСТОВ 2008',
    'form-piter': 'ФОМС САНКТ-ПЕТЕРБУРГ',
    'foms-saratov': 'ФОМС САРАТОВ 2008',
    'foms-sahalin': 'ФОМС САХАЛИН',
    'foms-sahalin-oblast': 'ФОМС САХАЛИНСКАЯ ОБЛАСТЬ',
    'foms-saratorv-2008': 'ФОМС РОСТОВ 2008',
    'foms-sahalin-2018': 'ФОМС САХАЛИН 2018',
    'moscov-residents2011': 'ЖИТЕЛИ МОСКВЫ 2011',
    'covid-2020': 'КОВИД 2020',
    'decline-credit-2006': 'ОТКАЗ В КРЕДИТЕ 2006',


    // ----------------- СТОПНУЛСЯ ТУТ -----------------\\
    'gibdd': 'ГИБДД',
    'zaimer': 'ЗАЙМЕР',
    'rf-driver-licenses-2020': 'Водительские права РФ 2020',
    'residents2018': 'Жители 2018',
    'mtsb': 'МТС-Б',
    'scoring': 'Скоринг',
    'residents2017': 'Жители 2017',
    'alphab2023': 'Альфа 2023 V2',
    'krasnoeibeloe': 'Красное и Белое',
    'taxpayers': 'Налогоплательщики',
    'docs': 'Документы',
    'alphabank2023': 'Альфа-Банк 2023',
    'housekeeper': 'Домоуправление',
    'mtsbank': 'МТС Банк',
    'residents2022': 'Жители 2022',
    'mfobig': 'МФО Большая',
    'oneclickmfo': 'МФО ВанКлик',
    'reestr2025': 'Реестр 2025',
    'moscowregistration': 'Московская регистрация',
    'residents2019': 'Жители 2019',
    'techodepartament': 'Техдепартамент',
    'headhunter': 'HeadHunter',
    'fssp2021': 'ДОЛЖНИКИ ФЛ ФССП 2021',
    'fssp2022': 'ДОЛЖНИКИ ФЛ ФССП 2022',
    'fssp2023': 'ДОЛЖНИКИ ФЛ ФССП 2023',
    'telegram-2022': 'Телеграм юзеры 2022',
    'open-broker': 'Брокеры 2021',
    'getcontact2': 'GetContact-2',
    'getcontact': 'GetContact',
    'k-vrachu': 'К-Врачу',
    'bolshaya-peremena1': 'Большая Перемена-1',
    'bolshaya-peremena2': 'Большая Перемена-2',
    'bolshaya-peremena3': 'Большая Перемена-3',
    'bolshaya-peremena4': 'Большая Перемена-4',
    'bolshaya-peremena5': 'Большая Перемена-5',
    'bolshaya-peremena6': 'Большая Перемена-6',
    'kia': 'КИА - авто',
    'covid': 'Ковид',
    'sdmed': 'СД-Мед',
    'bestworldbase': 'Ликвид',
    'vodokanal': 'Водоканал',
    'elections_istra_da': 'Выборы депутаты',
    'pleer': 'Плеер.ру',
    'numbuster': 'Numbuster',
    'numbuster2': 'Numbuster-2',
    'gosuslugi2023': 'Госуслуги 2023',
    'vip-med': 'Вип клиенты',
    'zdravcity2023': 'Пользователи Здравсити 2023',
    'residents2013': 'Жители 2013',
    'sportmastercleaned': 'Спортмастер',
    'himeratop': 'Химера',
    'russian_soldiers': 'Российские военнослужащие',
    'barahla': 'Барахло',
    'forabankmsk': 'Фора банк МСК',
    'givddmsk2018': 'ГИБДД МСК 2018',
    'gsm': 'GSM',
    'rfnumbers': 'РФ номера',
    'stock': 'Склад',
    'vk': 'ВКонтакте',
    'geoplus': 'Геоплюс',
    'ruwhatsapp': 'Российский WhatsApp',
    'foms-sakhalin': 'ФОМС Сахалин',
    'medical-insurance': 'Медицинское страхование',
    'qzaem': 'Кзаем',
    'online-shoe-store-clients': 'Клиенты обувного магазина',
    'alpha-members': 'Альфа',
    'russia2008-2022': 'Россия 2008-2022',
    'mvd2023': 'МВД 2023',
    'mvd2022': 'МВД 2022',
    'premruton1': 'Премиум РУТОН',
    'premruton2': 'Премиум РУТОН',
    'premruton3': 'Премиум РУТОН',
    'passport-ufa-2016': 'Паспорта Уфа 2016',
    'passport-ufa-2015': 'Паспорта Уфа 2015',
    'passport-ufa-2015-05': 'Паспорта Уфа 2015',
    'passport-ufa-2015-03': 'Паспорта Уфа 2015',
    'passport-ufa-2014': 'Паспорта Уфа 2014',
    'passport-ufa-2014-06': 'Паспорта Уфа 2014',
    'passport-ufa-2013': 'Паспорта Уфа 2013',
    'himpassport': 'Хим паспорт',
    'lada2022': 'Лада 2022',
    'vologod2008': 'Вологда 2008',
    'itmsk2025': 'ИТ МСК 2025',
    'msk2003': 'МСК 2003',
    'residents2025': 'Жители 2025',
    'gruz200': 'Груз 200 (СВО)',
    'gruz-200-2022': 'Груз 200 (СВО) 2022',
    'gruz-200-2023': 'Груз 200 (СВО) 2023',
    'gruz-200-2024': 'Груз 200 (СВО) 2024',
    'gruz-200-2025': 'Груз 200 (СВО) 2025',
    'relation-3': 'Возможные связи по адресу 2024',
    'moscow-dit': 'Департамент Информационных Технологий Москвы 2025',
    'russia-income-2016': 'Доходы физлиц 2016',
    'russia-residents-2025-05': 'Жители 2025',
    'gibbd-kirov': 'ГИБДД КИРОВ 2010',
    'dobro2': 'ПОЛЬЗОВАТЕЛИ DOBRO.RU 2022',
    'mybusiness': 'Пользователи мойбизнес.рф 2023',
    'russiannumbers': 'Абоненты 2015',
    'readcity': 'КЛИЕНТЫ ЧИТАЙ-ГОРОД 2023',
    'gibddmoscow2018': 'МОС ГИБДД МОСКВА И МО 2019'
  };

  const getSourceDisplayName = (source: string) => {
    return sourceNames[source] || source;
  };

  // Функция для простого хеширования строки
  const simpleHash = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Конвертируем в 32-битное целое
    }
    return Math.abs(hash).toString(36).slice(0, 8);
  };

  // Функция для получения ID записи (приоритет серверному ID)
  const getRecordId = (source: string, item: SearchResult, index: number) => {
    // Если сервер предоставил ID, используем его
    if (item.record_id) {
      return item.record_id;
    }
    
    // Fallback: генерируем ID на клиенте
    const identifiers = [
      item.id,
      item._id,
      item.system_number,
      item.passport,
      item.inn,
      item.snils,
      item.phone,
      item.phones?.[0],
      item.email
    ].filter(Boolean);
    
    if (identifiers.length > 0) {
      // Очищаем идентификатор от специальных символов для URL
      const cleanId = String(identifiers[0]).replace(/[^a-zA-Z0-9]/g, '');
      return `${source}-${cleanId}`;
    }
    
    // Если нет уникальных полей, используем хеш от содержимого
    const contentHash = simpleHash(JSON.stringify(item));
    return `${source}-${index}-${contentHash}`;
  };

  // Функция для создания permalink
  const createPermalink = (recordId: string) => {
    const currentUrl = new URL(window.location.href);
    currentUrl.hash = `record-${recordId}`;
    return currentUrl.toString();
  };

  // Функция для копирования ссылки
  const copyPermalink = async (recordId: string) => {
    const permalink = createPermalink(recordId);
    try {
      await navigator.clipboard.writeText(permalink);
      setCopiedId(recordId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Ошибка при копировании ссылки:', err);
    }
  };

  // Функция для сортировки полей по приоритету
  const sortFieldsByPriority = (fields: [string, any][]) => {
    return fields.sort(([keyA], [keyB]) => {
      const priorityA = fieldPriorities[keyA] || 999; // Неизвестные поля в конец
      const priorityB = fieldPriorities[keyB] || 999;
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // Если приоритеты одинаковые, сортируем по алфавиту
      return keyA.localeCompare(keyB);
    });
  };

  // Функция для дедупликации записей внутри источника
  const deduplicateRecords = (items: SearchResult[]) => {
    const recordMap = new Map<string, { record: SearchResult; count: number; originalIndex: number }>();
    
    items.forEach((item, index) => {
      // Создаем ключ для сравнения записей (исключаем служебные поля)
      const compareObject = { ...item };
      delete compareObject.record_id;
      delete compareObject._id;
      delete compareObject.id;
      
      const recordKey = JSON.stringify(compareObject, Object.keys(compareObject).sort());
      
      if (recordMap.has(recordKey)) {
        // Увеличиваем счетчик дубликатов
        const existing = recordMap.get(recordKey)!;
        existing.count += 1;
      } else {
        // Добавляем новую уникальную запись
        recordMap.set(recordKey, {
          record: item,
          count: 1,
          originalIndex: index
        });
      }
    });
    
    return Array.from(recordMap.values());
  };

  const renderFieldValue = (key: string, value: any) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    // Специальная обработка для некоторых полей
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  };

  if (Object.keys(results).length === 0 && !operatorInfo) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Результаты поиска</CardTitle>
          <CardDescription>
            Результаты поиска будут отображены здесь
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            Выполните поиск, чтобы увидеть результаты
          </div>
        </CardContent>
      </Card>
    );
  }

  // Функция для скролла к источнику
  const scrollToSource = (source: string) => {
    const element = document.getElementById(`source-${source}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="flex gap-6">
      {/* Боковая панель с источниками */}
      {Object.keys(results).length > 0 && (
        <div className="w-64 flex-shrink-0">
          <div className="sticky top-4">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-lg text-black">Источники</CardTitle>
                <CardDescription>
                  Найдено в {Object.keys(results).length} источниках
                </CardDescription>
              </CardHeader>
              <CardContent className="max-h-[600px] overflow-y-auto">
                <div className="space-y-2">
                  {Object.entries(results).map(([source, items]) => (
                    <button
                      key={source}
                      onClick={() => scrollToSource(source)}
                      className="w-full text-left p-3 rounded-lg border border-border hover:bg-accent transition-colors bg-card"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium text-card-foreground flex-1 min-w-0">
                          {getSourceDisplayName(source)}
                        </div>
                        <Badge variant="secondary" className="text-xs flex-shrink-0">
                          {items.length}
                        </Badge>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Основной контент */}
      <div className="flex-1 space-y-6">
        {/* Информация об операторе для поиска по телефону */}
        {operatorInfo && (
        <Card>
          <CardHeader>
            <CardTitle>Информация об операторе</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="font-medium">Номер:</span> {operatorInfo.phone}
              </div>
              <div>
                <span className="font-medium">Оператор:</span> {operatorInfo.operator}
              </div>
              <div>
                <span className="font-medium">Регион:</span> {operatorInfo.region}
              </div>
              <div>
                <span className="font-medium">Страна:</span> {operatorInfo.country}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

        {/* Результаты по источникам */}
        {Object.entries(results).map(([source, items]) => {
          const deduplicatedItems = deduplicateRecords(items);
          const totalOriginalRecords = items.length;
          const uniqueRecords = deduplicatedItems.length;
          
          return (
            <Card key={source} id={`source-${source}`} className="scroll-mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {getSourceDisplayName(source)}
                  <Badge variant="secondary">{uniqueRecords}</Badge>
                  {totalOriginalRecords !== uniqueRecords && (
                    <Badge variant="outline" className="text-xs">
                      из {totalOriginalRecords}
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  {totalOriginalRecords === uniqueRecords 
                    ? `Найдено ${uniqueRecords} записей в источнике "${getSourceDisplayName(source)}"`
                    : `Найдено ${uniqueRecords} уникальных записей из ${totalOriginalRecords} в источнике "${getSourceDisplayName(source)}"`
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {deduplicatedItems.map((dedupItem, index) => {
                    const { record: item, count } = dedupItem;
                    const recordId = getRecordId(source, item, dedupItem.originalIndex);
                    
                    return (
                      <div 
                        key={index} 
                        id={`record-${recordId}`}
                        className="border rounded-lg p-4 scroll-mt-4"
                      >
                        {/* Показываем количество дубликатов, если больше 1 */}
                        {count > 1 && (
                          <div className="mb-3 flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">
                              Дубликатов: {count}
                            </Badge>
                          </div>
                        )}
                        
                        <div className="space-y-3">
                          {sortFieldsByPriority(Object.entries(item))
                            .map(([key, value]) => {
                              // Пропускаем служебное поле record_id
                              if (key === 'record_id') return null;
                              
                              const displayValue = renderFieldValue(key, value);
                              if (!displayValue) return null;
                              
                              return (
                                <div key={key} className="flex flex-col sm:flex-row sm:items-center gap-2 py-1">
                                  <div className="text-sm font-medium text-muted-foreground min-w-0 sm:w-48 flex-shrink-0">
                                    {key}:
                                  </div>
                                  <div className="text-sm break-words flex-1">
                                    {displayValue}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Кнопка "Вверх" */}
      {showScrollTop && (
        <Button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 rounded-full w-12 h-12 shadow-lg text-xl"
          size="icon"
        >
          ↑
        </Button>
      )}
    </div>
  );
}
